import { useCallback, useEffect, useRef, useState } from 'react'
import {
  acceptCandidate,
  approveCandidateCrm,
  dismissCandidateCrm,
  listCandidates,
  rejectCandidate,
} from '../services/prospectingApi'
import type { CandidateStatus, ProspectCandidate } from '../types'

export type CandidateAction =
  | { kind: 'accept' }
  | { kind: 'reject'; reason?: string }
  | { kind: 'approve_crm' }
  | { kind: 'dismiss_crm' }

/**
 * One status tab of the review queue. A reviewed card stays where it is,
 * showing its new state, until the tab is reloaded — so the rep sees what
 * their click did (including the HubSpot outcome) instead of it vanishing.
 */
export function useProspectCandidates(workspaceId: string, status: CandidateStatus) {
  const [candidates, setCandidates] = useState<ProspectCandidate[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  // The status the current list belongs to — until a switched tab's fetch has
  // started, the previous tab's cards must not render under the new tab.
  const [loadedStatus, setLoadedStatus] = useState<CandidateStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({})
  const requestId = useRef(0)

  const load = useCallback(() => {
    const id = ++requestId.current
    setLoadedStatus(status)
    setLoading(true)
    setError(null)
    setActionErrors({})
    listCandidates(workspaceId, status)
      .then((page) => {
        if (id !== requestId.current) return
        setCandidates(page.candidates)
        setNextCursor(page.nextCursor)
      })
      .catch((err) => { if (id === requestId.current) setError(err instanceof Error ? err.message : 'Failed to load candidates') })
      .finally(() => { if (id === requestId.current) setLoading(false) })
  }, [workspaceId, status])

  useEffect(() => { load() }, [load])

  const loadMore = useCallback(() => {
    if (!nextCursor) return
    const id = requestId.current
    setLoadingMore(true)
    listCandidates(workspaceId, status, nextCursor)
      .then((page) => {
        if (id !== requestId.current) return
        setCandidates((prev) => [...prev, ...page.candidates])
        setNextCursor(page.nextCursor)
      })
      .catch((err) => { if (id === requestId.current) setError(err instanceof Error ? err.message : 'Failed to load candidates') })
      .finally(() => setLoadingMore(false))
  }, [workspaceId, status, nextCursor])

  const act = useCallback(async (candidateId: string, action: CandidateAction) => {
    setPendingIds((prev) => new Set(prev).add(candidateId))
    setActionErrors(({ [candidateId]: _cleared, ...rest }) => rest)
    try {
      const updated =
        action.kind === 'accept' ? await acceptCandidate(workspaceId, candidateId)
        : action.kind === 'reject' ? await rejectCandidate(workspaceId, candidateId, action.reason)
        : action.kind === 'approve_crm' ? await approveCandidateCrm(workspaceId, candidateId)
        : await dismissCandidateCrm(workspaceId, candidateId)
      setCandidates((prev) => prev.map((c) => (c.id === candidateId ? updated : c)))
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [candidateId]: err instanceof Error ? err.message : 'Action failed' }))
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev)
        next.delete(candidateId)
        return next
      })
    }
  }, [workspaceId])

  return { candidates, nextCursor, loading: loading || loadedStatus !== status, loadingMore, error, reload: load, loadMore, act, pendingIds, actionErrors }
}
